import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Tab, Tabs, Alert } from 'react-bootstrap';
import { MdSave, MdSecurity, MdNotifications, MdAccountCircle, MdPalette, MdLanguage } from 'react-icons/md';

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [settings, setSettings] = useState({
    profile: {
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      phone: '+1-555-0123',
      company: 'Acme Corp'
    },
    security: {
      twoFactor: false,
      emailNotifications: true,
      loginAlerts: true
    },
    notifications: {
      email: true,
      push: false,
      sms: false,
      weekly: true,
      monthly: false
    },
    appearance: {
      theme: 'light',
      language: 'en',
      timezone: 'America/New_York'
    }
  });

  const handleInputChange = (section: string, field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: value
      }
    }));
  };

  const handleSave = () => {
    // Simulate saving settings
    console.log('Settings saved:', settings);
    // Show success message
  };

  return (
    <Container fluid className="py-4">
      <div className="page-header mb-4">
        <h2>System Settings</h2>
        <p className="text-muted">Manage your account settings and preferences</p>
      </div>

      <Row>
        <Col lg={3}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Settings Categories</h6>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="settings-nav">
                <div 
                  className={`settings-nav-item ${activeTab === 'profile' ? 'active' : ''}`}
                  onClick={() => setActiveTab('profile')}
                >
                  <MdAccountCircle className="me-2" />
                  Profile
                </div>
                <div 
                  className={`settings-nav-item ${activeTab === 'security' ? 'active' : ''}`}
                  onClick={() => setActiveTab('security')}
                >
                  <MdSecurity className="me-2" />
                  Security
                </div>
                <div 
                  className={`settings-nav-item ${activeTab === 'notifications' ? 'active' : ''}`}
                  onClick={() => setActiveTab('notifications')}
                >
                  <MdNotifications className="me-2" />
                  Notifications
                </div>
                <div 
                  className={`settings-nav-item ${activeTab === 'appearance' ? 'active' : ''}`}
                  onClick={() => setActiveTab('appearance')}
                >
                  <MdPalette className="me-2" />
                  Appearance
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={9}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                {activeTab === 'profile' && 'Profile Settings'}
                {activeTab === 'security' && 'Security Settings'}
                {activeTab === 'notifications' && 'Notification Settings'}
                {activeTab === 'appearance' && 'Appearance Settings'}
              </h5>
              <Button variant="primary" onClick={handleSave}>
                <MdSave className="me-1" />
                Save Changes
              </Button>
            </Card.Header>
            <Card.Body>
              {activeTab === 'profile' && (
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>First Name</Form.Label>
                        <Form.Control 
                          type="text" 
                          value={settings.profile.firstName}
                          onChange={(e) => handleInputChange('profile', 'firstName', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control 
                          type="text" 
                          value={settings.profile.lastName}
                          onChange={(e) => handleInputChange('profile', 'lastName', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control 
                          type="email" 
                          value={settings.profile.email}
                          onChange={(e) => handleInputChange('profile', 'email', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control 
                          type="tel" 
                          value={settings.profile.phone}
                          onChange={(e) => handleInputChange('profile', 'phone', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Company</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={settings.profile.company}
                      onChange={(e) => handleInputChange('profile', 'company', e.target.value)}
                    />
                  </Form.Group>
                </Form>
              )}

              {activeTab === 'security' && (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="twoFactor"
                      label="Two-Factor Authentication"
                      checked={settings.security.twoFactor}
                      onChange={(e) => handleInputChange('security', 'twoFactor', e.target.checked)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="emailNotifications"
                      label="Email Security Notifications"
                      checked={settings.security.emailNotifications}
                      onChange={(e) => handleInputChange('security', 'emailNotifications', e.target.checked)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="loginAlerts"
                      label="Login Alerts"
                      checked={settings.security.loginAlerts}
                      onChange={(e) => handleInputChange('security', 'loginAlerts', e.target.checked)}
                    />
                  </Form.Group>
                  <Button variant="outline-primary" className="me-2">
                    Change Password
                  </Button>
                  <Button variant="outline-secondary">
                    View Login History
                  </Button>
                </Form>
              )}

              {activeTab === 'notifications' && (
                <Form>
                  <h6 className="mb-3">Notification Channels</h6>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="email"
                      label="Email Notifications"
                      checked={settings.notifications.email}
                      onChange={(e) => handleInputChange('notifications', 'email', e.target.checked)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="push"
                      label="Push Notifications"
                      checked={settings.notifications.push}
                      onChange={(e) => handleInputChange('notifications', 'push', e.target.checked)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="sms"
                      label="SMS Notifications"
                      checked={settings.notifications.sms}
                      onChange={(e) => handleInputChange('notifications', 'sms', e.target.checked)}
                    />
                  </Form.Group>
                  
                  <h6 className="mb-3 mt-4">Notification Frequency</h6>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="weekly"
                      label="Weekly Summary"
                      checked={settings.notifications.weekly}
                      onChange={(e) => handleInputChange('notifications', 'weekly', e.target.checked)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="monthly"
                      label="Monthly Report"
                      checked={settings.notifications.monthly}
                      onChange={(e) => handleInputChange('notifications', 'monthly', e.target.checked)}
                    />
                  </Form.Group>
                </Form>
              )}

              {activeTab === 'appearance' && (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Theme</Form.Label>
                    <Form.Select 
                      value={settings.appearance.theme}
                      onChange={(e) => handleInputChange('appearance', 'theme', e.target.value)}
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="auto">Auto</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Language</Form.Label>
                    <Form.Select 
                      value={settings.appearance.language}
                      onChange={(e) => handleInputChange('appearance', 'language', e.target.value)}
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Timezone</Form.Label>
                    <Form.Select 
                      value={settings.appearance.timezone}
                      onChange={(e) => handleInputChange('appearance', 'timezone', e.target.value)}
                    >
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="Europe/London">London</option>
                      <option value="Europe/Paris">Paris</option>
                    </Form.Select>
                  </Form.Group>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Settings;
